import { z } from 'zod';

// Skills and careers (the profile's Skills and Career tabs). Both grow from what the character does; skills
// rust when left unused; every number lives here so the server and the app agree.

export const SKILLS = {
  cooking: { label: 'Cooking', icon: '🍳', description: 'Better meals, and they fill you up more.' },
  charisma: { label: 'Charisma', icon: '😎', description: 'People warm to you faster.' },
  fitness: { label: 'Fitness', icon: '🏃', description: 'Workouts and hard graft tire you less.' },
  coding: { label: 'Coding', icon: '💻', description: 'Office work and study come easier.' },
  music: { label: 'Music', icon: '🎸', description: 'Busking and karaoke go down better.' },
  hustle: { label: 'Hustle', icon: '💼', description: 'Market stalls and side gigs come naturally.' },
  dance: { label: 'Dance', icon: '💃', description: 'Own the dance floor.' },
  comedy: { label: 'Comedy', icon: '🎤', description: 'Get the laughs at open mic nights.' },
  photography: { label: 'Photography', icon: '📷', description: 'See London through a lens.' },
} as const;
export type SkillKey = keyof typeof SKILLS;
export const SKILL_KEYS = Object.keys(SKILLS) as SkillKey[];

// XP needed for each level (index = level); level 10 is the top
export const SKILL_LEVELS = [0, 50, 150, 300, 500, 800, 1200, 1700, 2300, 3000, 4000] as const;
export const MAX_SKILL_LEVEL = SKILL_LEVELS.length - 1;
export function skillLevel(xp: number) {
  let level = 0;
  while (level < MAX_SKILL_LEVEL && xp >= SKILL_LEVELS[level + 1]) level += 1;
  return level;
}
// how far through the current level (0 to 1); 1 at the top level
export function skillProgress(xp: number) {
  const level = skillLevel(xp);
  if (level === MAX_SKILL_LEVEL) return 1;
  return (xp - SKILL_LEVELS[level]) / (SKILL_LEVELS[level + 1] - SKILL_LEVELS[level]);
}
// a skill left unused for more than 3 days loses 3% of its XP a day (it can drop a level)
export const RUST_AFTER_DAYS = 3;
export const RUST_PER_DAY = 0.03;
export function skillXpAt(xp: number, lastTrainedAt: string | Date | null, now: Date = new Date()) {
  if (!lastTrainedAt || xp <= 0) return Math.max(0, xp);
  const idleDays = (now.getTime() - new Date(lastTrainedAt).getTime()) / 86_400_000 - RUST_AFTER_DAYS;
  if (idleDays <= 0) return xp;
  return Math.max(0, Math.floor(xp * (1 - RUST_PER_DAY) ** idleDays));
}
export const isRusting = (lastTrainedAt: string | null, now: Date = new Date()) =>
  Boolean(lastTrainedAt) && now.getTime() - Date.parse(lastTrainedAt!) > RUST_AFTER_DAYS * 86_400_000;

// what an activity trains and whether it is a job come from the content (ActivityContent.skills and .job)
type Trains = { skills?: Partial<Record<SkillKey, number>> };
// a skill makes the activities it leads go better: +3% to what they fill per level
export function skillBoost(activity: Trains | undefined, levels: Partial<Record<SkillKey, number>>) {
  const lead = Object.keys(activity?.skills ?? {})[0] as SkillKey | undefined;
  return lead ? 1 + 0.03 * (levels[lead] ?? 0) : 1;
}

// shifts needed for each career level (index = level - 1); a certificate for the job eases them (courses.ts:
// `eased` 0.15 means 15% fewer shifts for each level)
export const CAREER_LEVELS = [0, 10, 30, 60, 100] as const;
const levelShifts = (level: number, eased: number) => Math.round(CAREER_LEVELS[level]! * (1 - eased));
export function careerLevel(shifts: number, eased = 0) {
  let level = 1;
  while (level < CAREER_LEVELS.length && shifts >= levelShifts(level, eased)) level += 1;
  return level;
}
// each career level adds 5% to the wage (the £300 daily cap still applies)
export const RAISE_PER_LEVEL = 0.05;
export function wageFor(baseWagePence: number, shifts: number, eased = 0) {
  return Math.round(baseWagePence * (1 + RAISE_PER_LEVEL * (careerLevel(shifts, eased) - 1)));
}
export const careerTitle = (job: { job?: { titles: string[] } }, shifts: number, eased = 0) =>
  job.job?.titles[careerLevel(shifts, eased) - 1] ?? '';
export const shiftsToNextLevel = (shifts: number, eased = 0) => {
  const level = careerLevel(shifts, eased);
  return level >= CAREER_LEVELS.length ? null : levelShifts(level, eased) - shifts;
};
// the shifts a level starts at (for "N more to become …")
export const shiftsForLevel = (level: number, eased = 0) => levelShifts(level - 1, eased);

// what the game state carries: each skill's XP (as last saved) and when it was last trained; shifts per job
export const Progress = z.object({
  skills: z.record(z.string(), z.object({ xp: z.number().int().min(0), lastTrainedAt: z.string().nullable() })),
  jobs: z.record(z.string(), z.number().int().min(0)),
  // perks owned, and lessons passed per course enrolled on (courses.ts)
  perks: z.array(z.string()),
  courses: z.record(z.string(), z.number().int().min(0)),
});
export type Progress = z.infer<typeof Progress>;
export const EMPTY_PROGRESS: Progress = { skills: {}, jobs: {}, perks: [], courses: {} };
// every skill's level now, rust included
export function skillLevelsAt(progress: Progress, now: Date = new Date()) {
  return Object.fromEntries(
    SKILL_KEYS.map((key) => {
      const saved = progress.skills[key];
      return [key, saved ? skillLevel(skillXpAt(saved.xp, saved.lastTrainedAt, now)) : 0];
    }),
  ) as Record<SkillKey, number>;
}
